<script>
import FileBrowser from '@/components/file-browser/FileBrowser';
import Panel from '@/components/ui/Panel';
import escapeHtml from 'escape-html';
import { dirname, join, removeBasePath } from '@/modules/file-utilities';
import URI from 'urijs';

export default {
  name: 'ImagePanel',
  components: {
    FileBrowser,
    Panel
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      editor: {},
      bookmark: null,
      caption: '',
      enableCaption: false,
      image: null,
      isFileBrowserOpen: false,
      src: '',
      alt: '',
      align: 'none'
    };
  },
  methods: {
    show(options) {
      const editor = options.editor;
      const element = options.editor ? editor.getSelectedElement() : options.element;
      const figure = element.closest('figure.image');
      const figcaption = figure ? figure.querySelector('figcaption') : null;
      const image = figure ? figure.querySelector('img') : element.closest('img');

      this.editor = editor;
      this.image = image;
      this.caption = figcaption ? figcaption.innerHTML : '';

      if (editor) {
        // Get attributes from an image in the editor
        this.bookmark = editor.getBookmark();
        this.src = this.image ? decodeURI(editor.getAttribute(this.image, 'src')) : '';
        this.alt = this.image ? editor.getAttribute(this.image, 'alt') : '';
        this.enableCaption = this.image ? !!this.image.closest('figure') : false;
        this.align = 'none';
        if (image) {
          if (editor.isAlignLeft()) this.align = 'left';
          if (editor.isAlignCenter()) this.align = 'center';
          if (editor.isAlignRight()) this.align = 'right';
        }
      } else {
        // Get attributes from an editable image
        this.src = decodeURI(image.getAttribute('src'));
        this.alt = image.getAttribute('alt');
        this.align = 'none';
      }

      this.$refs.panel.show();
    },

    hide() {
      this.$refs.panel.hide();
      this.src = '';
      this.alt = '';
      this.align = 'none';
    },

    deleteImage() {
      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      this.editor.removeElement(this.image);
    },

    handleFileBrowserSelect(type, files) {
      if (files.length) {
        if (type === 'upload') {
          const file = files[0];
          const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
          const fileUrl = new URI(this.site.url).path(
            URI.joinPaths(this.site.url, removeBasePath(file.path, this.site.homeFolder))
          );
          const src = fileUrl.relativeTo(pageUrl).toString();

          this.src = src;
          this.height = '';
          this.$refs.fileBrowserPopover.doClose();
        } else if (type === 'unsplash') {
          const src = files[0].urls.regular;
          const alt = files[0].description;
          const name = files[0].user.name;
          const username = files[0].user.username;
          const queryString = '?utm_source=Surreal%20CMS&utm_medium=referral';

          this.src = src;
          this.alt = alt;
          this.height = '';
          this.caption = `Photo by <a href="https://unsplash.com/@${escapeHtml(username)}${queryString}">${escapeHtml(
            name
          )}</a> on <a href="https://unsplash.com/${queryString}" rel="noopener">Unsplash</a>`;
          this.enableCaption = true;
          this.$refs.fileBrowserPopover.doClose();
        }
      }
    },

    handleHide() {
      this.$refs.fileBrowserPopover.doClose();
      if (this.editor) {
        this.editor.focus();
      }
    },

    handleShow() {
      this.$refs.src.focus();
    },

    handleSubmit() {
      const editor = this.editor;
      const image = this.image;
      const src = this.src || '';
      const alt = this.alt || '';
      const align = this.align;
      const caption = this.enableCaption ? this.caption || this.$t('Caption') : false;

      this.$refs.panel.hide();

      // No src means the image gets removed
      if (src === '') {
        this.deleteImage();
        return;
      }

      if (editor) {
        // Set attributes on an image in the editor
        editor.restoreBookmark(this.bookmark);
        editor.insertImage(src, {
          alt,
          align,
          caption
        });
      } else {
        // Set attributes on an editable image
        image.setAttribute('src', src);
        image.setAttribute('alt', alt);
      }
    },

    showFileBrowser() {
      let path = this.site.uploadFolder;

      // If the current src looks like a relative path, use that directory
      if (!this.src.match(/^https?:\/\//i)) {
        const fileUrl = new URI(this.src);
        path = join(this.site.homeFolder, dirname(fileUrl.path()));
      }

      this.isFileBrowserOpen = true;
      this.$refs.fileBrowser.open({ path });
    },

    hideFileBrowser() {
      this.$refs.fileBrowser.close();
      this.isFileBrowserOpen = false;
    }
  }
};
</script>

<template>
  <panel ref="panel" :title="$t('Image')" :close-on-click="!isFileBrowserOpen" @show="handleShow" @hide="handleHide">
    <el-form label-position="top" novalidate @submit.native.prevent="handleSubmit">
      <fieldset>
        <!-- URL -->
        <el-form-item :label="$t('URL')">
          <el-input
            ref="src"
            v-model="src"
            type="url"
            clearable
            prefix-icon="el-icon-fa-image"
            auto-complete="off"
            spellcheck="false"
          >
            <template slot="append">
              <el-popover
                ref="fileBrowserPopover"
                placement="bottom"
                trigger="click"
                @show="showFileBrowser()"
                @hide="hideFileBrowser()"
              >
                <el-button slot="reference">
                  {{ $t('Browse') }}
                </el-button>

                <file-browser
                  ref="fileBrowser"
                  :base-path="site.uploadFolder"
                  :can-select="file => file.type === 'file' && file.name.match(/\.(gif|jpeg|jpg|png|svg|webp)$/i)"
                  :multiple="false"
                  :site="site"
                  :show-unsplash="true"
                  type="popover"
                  @select="handleFileBrowserSelect"
                />
              </el-popover>
            </template>
          </el-input>
        </el-form-item>

        <!-- Description -->
        <el-form-item :label="$t('Description')">
          <el-input v-model="alt" type="textarea" auto-complete="off" />
          <div class="text-form-description">
            {{ $t("Alternative text for users who can't see images.") }}
          </div>
        </el-form-item>

        <!-- Alignment -->
        <el-form-item v-if="editor" :label="$t('Alignment')" class="alignment">
          <el-radio-group v-model="align">
            <el-radio-button label="none">
              {{ $t('None') }}
            </el-radio-button>
            <el-radio-button :title="$t('Align Left')" label="left">
              <i class="el-icon-fa-align-left" />
            </el-radio-button>
            <el-radio-button :title="$t('Align Center')" label="center">
              <i class="el-icon-fa-align-center" />
            </el-radio-button>
            <el-radio-button :title="$t('Align Right')" label="right">
              <i class="el-icon-fa-align-right" />
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <!-- Caption -->
        <el-form-item v-if="editor">
          <el-switch v-model="enableCaption" :active-text="$t('Caption')" />
        </el-form-item>

        <!-- Actions -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-button type="primary" native-type="submit">
              <span v-if="image">{{ $t('Update Image') }}</span>
              <span v-else>{{ $t('Insert Image') }}</span>
            </el-button>
          </el-col>
          <el-col v-if="editor" :span="12" class="text-right">
            <el-button v-if="image && editor" :title="$t('Delete Image')" @click="deleteImage">
              <i class="el-icon-fa-trash" />
            </el-button>
          </el-col>
        </el-row>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.alignment {
  margin-bottom: 1.5rem;
}
</style>
